import { useCallback } from 'react';
import { forgePayloadToMutationArgs } from '../components/chrome/forge-v2/forgeAdapter';

const rawMessage = (err) => err?.message || String(err);

const humanizeWarning = (label, err) => {
  const message = rawMessage(err);
  if (label === 'Source campaign import' && /dev[\s-]*only/i.test(message)) {
    return `${label}: existing-campaign import is only available to beta admins right now.`;
  }
  return `${label}: ${message}`;
};

const partialSuccessError = ({ campaignId, inviteResult, result, warnings }) => {
  const err = new Error(
    `Campaign created, but some details did not save:\n- ${warnings.join('\n- ')}`,
  );
  err.partialSuccess = true;
  err.campaignId = campaignId;
  err.inviteCode = inviteResult?.code || result?.inviteCode || null;
  err.invite = inviteResult || null;
  return err;
};

// Orchestrates the Convex mutation chain for CampaignForgeV2.
//
// Step 1 (createCampaign) is hard-fail — the rest of the chain depends on
// it. Steps 2-5 (world selection / settings / invite / source import) are
// best-effort individually, but any partial failure is surfaced to the wizard
// after the chain finishes so a settings/import failure cannot look like a
// clean campaign creation.
//
// Production dashboard creation now routes through this hook; the older
// onCreateCampaign shape remains only as a minimal fallback.
export const useForgeSubmit = ({
  createCampaign,
  createCampaignWithSetup,
  updateCampaignSettings,
  createInvite,
  setActiveCampaign,
  applyCampaignWorldSelection,
  previewSourceCampaignImport,
  startSourceCampaignImport,
}) => useCallback(async (form) => {
  const {
    createArgs,
    settingsArgs,
    worldPlan,
    sourceImportPlan,
  } = forgePayloadToMutationArgs(form);

  if (createCampaignWithSetup) {
    const setupPayload = {
      ...createArgs,
      createInvite: true,
    };
    if (Object.keys(settingsArgs).length > 0) {
      setupPayload.settings = settingsArgs;
    }
    const result = await createCampaignWithSetup(setupPayload);
    const campaignId = result?.campaignId;
    if (!campaignId) {
      throw new Error('Campaign creation returned no campaignId');
    }

    const warnings = [];
    const inviteResult = result?.invite || (result?.inviteCode ? { code: result.inviteCode } : null);

    if (worldPlan?.mode === 'new' || worldPlan?.mode === 'existing') {
      try {
        if (!applyCampaignWorldSelection) {
          throw new Error('World selection API is not available');
        }
        const payload = {
          campaignId,
          worldMode: worldPlan.mode,
          ...(worldPlan.mode === 'new'
            ? {
                newWorldName: worldPlan.name,
                baseThemeId: form.baseTheme || undefined,
                baseVariant: form.baseVariant || undefined,
              }
            : { worldId: worldPlan.worldId }),
        };
        await applyCampaignWorldSelection(payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[forge] world selection failed; campaign was created', err, worldPlan);
        warnings.push(humanizeWarning('World selection', err));
      }
    }

    if (sourceImportPlan?.sourceCampaignId) {
      try {
        if (!previewSourceCampaignImport || !startSourceCampaignImport) {
          throw new Error('Source campaign import API is not available');
        }
        const preview = await previewSourceCampaignImport({
          campaignId,
          sourceCampaignId: sourceImportPlan.sourceCampaignId,
        });
        if (preview?.errors?.length > 0) {
          throw new Error(preview.errors.join('; '));
        }
        const importResult = await startSourceCampaignImport({
          campaignId,
          sourceCampaignId: sourceImportPlan.sourceCampaignId,
          expectedPreviewJobKey: preview?.jobKey,
          confirmed: true,
        });
        if (importResult?.status && importResult.status !== 'complete') {
          throw new Error((importResult.errors || []).join('; ') || `source import ${importResult.status}`);
        }
        if (importResult?.errors?.length > 0) {
          throw new Error(importResult.errors.join('; '));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[forge] source campaign import failed; campaign was created', err, sourceImportPlan);
        warnings.push(humanizeWarning('Source campaign import', err));
      }
    }

    if (setActiveCampaign) {
      try {
        await setActiveCampaign({ campaignId });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[forge] setActiveCampaign failed', err);
      }
    }

    if (warnings.length > 0) {
      throw partialSuccessError({ campaignId, inviteResult, result, warnings });
    }

    return {
      campaignId,
      inviteCode: inviteResult?.code || result?.inviteCode || null,
      invite: inviteResult || null,
    };
  }

  // 1. Create the campaign. Hard-fail; bubble the error to the wizard so
  //    it can surface it inline.
  const result = await createCampaign(createArgs);
  const campaignId = result?.campaignId;
  if (!campaignId) {
    throw new Error('Campaign creation returned no campaignId');
  }

  // Collect non-fatal errors from the follow-up steps. The campaign exists
  // either way, but if world / settings / invite / source-import calls failed we
  // surface a composite error to the wizard so the user can see and act on
  // them. Per-step console.warn is preserved for grep-ability.
  const warnings = [];
  let inviteResult = null;

  // 2. Apply world selection before generic settings. This is intentionally
  //    a single server mutation so "create world + attach campaign" remains
  //    atomic and retryable. Isolated/non-scratch flows require no world
  //    mutation.
  if (worldPlan?.mode === 'new' || worldPlan?.mode === 'existing') {
    try {
      if (!applyCampaignWorldSelection) {
        throw new Error('World selection API is not available');
      }
      const payload = {
        campaignId,
        worldMode: worldPlan.mode,
        ...(worldPlan.mode === 'new'
          ? {
              newWorldName: worldPlan.name,
              baseThemeId: form.baseTheme || undefined,
              baseVariant: form.baseVariant || undefined,
            }
          : { worldId: worldPlan.worldId }),
      };
      await applyCampaignWorldSelection(payload);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[forge] world selection failed; campaign was created', err, worldPlan);
      warnings.push(humanizeWarning('World selection', err));
    }
  }

  // 3. Persist rich settings. Skip the call entirely when there's nothing
  //    to send.
  if (Object.keys(settingsArgs).length > 0) {
    try {
      await updateCampaignSettings({ campaignId, ...settingsArgs });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[forge] settings persist failed; campaign was created', err, settingsArgs);
      warnings.push(humanizeWarning('Settings (tagline, schedule, theme)', err));
    }
  }

  // 4. Existing-campaign import. Preview first so policy/source errors are
  // surfaced without starting a materialization job, then start the import
  // only if the preview is clean.
  if (sourceImportPlan?.sourceCampaignId) {
    try {
      if (!previewSourceCampaignImport || !startSourceCampaignImport) {
        throw new Error('Source campaign import API is not available');
      }
      const preview = await previewSourceCampaignImport({
        campaignId,
        sourceCampaignId: sourceImportPlan.sourceCampaignId,
      });
      if (preview?.errors?.length > 0) {
        throw new Error(preview.errors.join('; '));
      }
      const importResult = await startSourceCampaignImport({
        campaignId,
        sourceCampaignId: sourceImportPlan.sourceCampaignId,
        expectedPreviewJobKey: preview?.jobKey,
        confirmed: true,
      });
      if (importResult?.status && importResult.status !== 'complete') {
        throw new Error((importResult.errors || []).join('; ') || `source import ${importResult.status}`);
      }
      if (importResult?.errors?.length > 0) {
        throw new Error(importResult.errors.join('; '));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[forge] source campaign import failed; campaign was created', err, sourceImportPlan);
      warnings.push(humanizeWarning('Source campaign import', err));
    }
  }

  // 5. Generate an invite code. The forge implies the user wants one.
  try {
    inviteResult = await createInvite({ campaignId });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[forge] invite generation failed; can be added via Settings', err);
    warnings.push(humanizeWarning('Invite code', err));
  }

  // 6. Defensive: createCampaign already calls setActiveCampaignForUser
  //    server-side, but createCampaignCharacter also touches active state
  //    so re-assert here so the dashboard projects the new campaign as
  //    active even if no dummies were spawned.
  if (setActiveCampaign) {
    try {
      await setActiveCampaign({ campaignId });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[forge] setActiveCampaign failed', err);
    }
  }

  if (warnings.length > 0) {
    throw partialSuccessError({ campaignId, inviteResult, result: null, warnings });
  }

  return {
    campaignId,
    inviteCode: inviteResult?.code || null,
    invite: inviteResult || null,
  };
}, [
  createCampaign,
  createCampaignWithSetup,
  updateCampaignSettings,
  createInvite,
  setActiveCampaign,
  applyCampaignWorldSelection,
  previewSourceCampaignImport,
  startSourceCampaignImport,
]);
